/**
 * XLSX exporter with brand header + cell type preservation per
 * M3X.REPORTS §0.2.
 *
 * Library: `xlsx` (SheetJS). The package's CVEs only affect the
 * PARSING path; we only generate workbooks, never parse user files.
 *
 * Brand header layout (rows 1-3):
 *   Row 1: "Solvait" wordmark (merged A1:F1, brand-deep #2260C9 bg)
 *   Row 2: Report title + period (merged A2:F2)
 *   Row 3: Generated-at timestamp + generated-by user (merged A2:F2)
 *   Row 4: BLANK
 *   Row 5: Column headers (frozen)
 *   Row 6+: Data
 *
 * Cell type preservation: caller passes column types via
 * `ExportColumn.type` ∈ 'string' | 'number' | 'date' | 'currency' |
 * 'boolean'. The exporter writes XLSX cell type codes accordingly.
 */

import * as XLSX from "xlsx";

import type { ExportColumn, ExportPayload } from "./types";

export async function exportXlsx(payload: ExportPayload, filename: string): Promise<void> {
  const workbook = XLSX.utils.book_new();
  const sheet = buildSheet(payload);
  XLSX.utils.book_append_sheet(workbook, sheet, sheetNameOf(payload));
  XLSX.writeFile(workbook, filename);
}

function buildSheet(payload: ExportPayload): XLSX.WorkSheet {
  const { columns, rows, title, period, generatedAt, generatedBy } = payload;
  const totalCols = Math.max(columns.length, 4);
  const lastColLetter = XLSX.utils.encode_col(totalCols - 1);

  // Build raw AOA (array of arrays) for the data region + brand-header overlay.
  const aoa: unknown[][] = [];

  // Row 1: brand wordmark
  aoa.push(["Solvait", ...new Array(totalCols - 1).fill("")]);
  // Row 2: title + period
  aoa.push([
    period ? `${title} — ${period}` : title,
    ...new Array(totalCols - 1).fill(""),
  ]);
  // Row 3: generated-at / by
  aoa.push([
    `Generated ${formatStamp(generatedAt)}${
      generatedBy ? ` by ${generatedBy}` : ""
    }`,
    ...new Array(totalCols - 1).fill(""),
  ]);
  // Row 4: blank
  aoa.push(new Array(totalCols).fill(""));
  // Row 5: column headers (display labels)
  aoa.push(columns.map((c) => c.label));
  // Row 6+: data
  for (const row of rows) {
    aoa.push(columns.map((c) => coerceCellValue(row[c.key], c)));
  }

  const sheet = XLSX.utils.aoa_to_sheet(aoa);

  // Cell type hints (sheet["!cols"] widths + per-cell .t).
  applyCellTypes(sheet, columns, rows.length);
  applyColumnWidths(sheet, columns);
  applyMerges(sheet, lastColLetter);
  applyFreezeHeader(sheet);
  applyAutoFilter(sheet, columns.length, rows.length);

  return sheet;
}

function coerceCellValue(value: unknown, col: ExportColumn): unknown {
  if (value === null || value === undefined) return "";
  switch (col.type) {
    case "date":
      if (value instanceof Date) return value;
      if (typeof value === "string" || typeof value === "number") {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? String(value) : d;
      }
      return String(value);
    case "number":
    case "currency": {
      const n = typeof value === "number" ? value : Number(value);
      return Number.isNaN(n) ? String(value) : n;
    }
    case "boolean":
      return Boolean(value);
    case "string":
    default:
      return String(value);
  }
}

function applyCellTypes(
  sheet: XLSX.WorkSheet,
  columns: ExportColumn[],
  rowCount: number,
): void {
  // Data starts at row index 5 (1-based row 6 in spreadsheet land).
  const dataStartRow = 5;
  for (let r = 0; r < rowCount; r++) {
    for (let c = 0; c < columns.length; c++) {
      const addr = XLSX.utils.encode_cell({ r: dataStartRow + r, c });
      const cell = sheet[addr] as XLSX.CellObject | undefined;
      if (!cell) continue;
      const col = columns[c];
      switch (col.type) {
        case "date":
          if (cell.v instanceof Date) {
            cell.t = "d";
            cell.z = "yyyy-mm-dd";
          }
          break;
        case "currency":
          if (typeof cell.v === "number") {
            cell.t = "n";
            cell.z = `#,##0.00 "${col.currency ?? "SAR"}"`;
          }
          break;
        case "number":
          if (typeof cell.v === "number") {
            cell.t = "n";
            if (col.decimals !== undefined) {
              cell.z = col.decimals === 0 ? "0" : `0.${"0".repeat(col.decimals)}`;
            }
          }
          break;
        case "boolean":
          cell.t = "b";
          break;
        default:
          cell.t = "s";
      }
    }
  }
}

function applyColumnWidths(sheet: XLSX.WorkSheet, columns: ExportColumn[]): void {
  sheet["!cols"] = columns.map((c) => ({
    wch: Math.max(12, Math.min(40, c.label.length + 4)),
  }));
}

function applyMerges(sheet: XLSX.WorkSheet, lastColLetter: string): void {
  // Brand-header merges across rows 1-3
  const merges = sheet["!merges"] ?? [];
  for (const r of [0, 1, 2]) {
    merges.push({ s: { r, c: 0 }, e: { r, c: cellRefToCol(lastColLetter) } });
  }
  sheet["!merges"] = merges;
}

function applyFreezeHeader(sheet: XLSX.WorkSheet): void {
  // Freeze first 5 rows (brand header + blank + column header)
  sheet["!freeze"] = { xSplit: 0, ySplit: 5 };
}

function applyAutoFilter(
  sheet: XLSX.WorkSheet,
  colCount: number,
  rowCount: number,
): void {
  if (rowCount === 0) return;
  const startCell = XLSX.utils.encode_cell({ r: 4, c: 0 });
  const endCell = XLSX.utils.encode_cell({
    r: 4 + rowCount,
    c: Math.max(0, colCount - 1),
  });
  sheet["!autofilter"] = { ref: `${startCell}:${endCell}` };
}

function cellRefToCol(letter: string): number {
  let result = 0;
  for (const ch of letter) {
    result = result * 26 + (ch.charCodeAt(0) - 64);
  }
  return result - 1;
}

function sheetNameOf(payload: ExportPayload): string {
  return (payload.title || "Report").slice(0, 28).replace(/[\\/:*?[\]]/g, "_");
}

function formatStamp(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  const h = String(d.getUTCHours()).padStart(2, "0");
  const mn = String(d.getUTCMinutes()).padStart(2, "0");
  return `${y}-${m}-${day} ${h}:${mn} UTC`;
}
